import type { BaseDocument } from '../mongo/base-document.interface';
import type { ServicePlan, ServicePlanInterval } from '../service-plan/service-plan.interface';

/**
 * The STORED status of a service reminder (SWOOP-247 / SWOOP-306). It records what a person or an
 * event did to the reminder — never how urgent it is. Urgency ({@link SERVICE_REMINDER_URGENCY}) is
 * worked out against today on every read, because there is no nightly job (247 story 5, 1 Oct).
 *
 * - `ACTIVE` — tracked normally; its urgency is whatever `deriveUrgency` says today.
 * - `SNOOZED` — deferred with a reason (SWOOP-281). Not written by 247; the value exists so the model
 *   holds it from the start.
 * - `DISMISSED` — this occurrence was dismissed with a reason (SWOOP-281). Not written by 247.
 * - `CANCELLED` — the vehicle no longer follows this interval (interval removed, vehicle no longer
 *   matches, another plan won, plan deactivated or expired). History is kept; the row is never deleted.
 * - `COMPLETED` — a work order opened from this reminder was CLOSED (SWOOP-291, WOW 4): the row is frozen
 *   as history ({@link ServiceReminder.completedAt}, `completedOdometer`, the closing work order kept on
 *   it) and a NEW `ACTIVE` row is created for the next cycle. A completed row is never live: it is never
 *   listed in an urgency tab, snoozed, dismissed, linked to a work order, or touched by the plan sync.
 *
 * There is deliberately no `SCHEDULED`: a reminder with an open work order keeps its live status (`ACTIVE`
 * or `SNOOZED`) and carries the work order as a marker ({@link ServiceReminder.workOrderId}); the value
 * was removed in SWOOP-291 (WOW 4) because nothing ever wrote it.
 */
export const SERVICE_REMINDER_STATUS = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
  DISMISSED: 'DISMISSED',
  SNOOZED: 'SNOOZED',
} as const;

export type ServiceReminderStatus = (typeof SERVICE_REMINDER_STATUS)[keyof typeof SERVICE_REMINDER_STATUS];

/**
 * How urgent a reminder is right now (SWOOP-306). Never stored — derived from the stored due point and
 * today by `deriveUrgency`. `OVERDUE` is past the due point, `DUE` is inside the interval's own
 * grace window, `UPCOMING` is before it.
 */
export const SERVICE_REMINDER_URGENCY = {
  DUE: 'DUE',
  OVERDUE: 'OVERDUE',
  UPCOMING: 'UPCOMING',
} as const;

export type ServiceReminderUrgency = (typeof SERVICE_REMINDER_URGENCY)[keyof typeof SERVICE_REMINDER_URGENCY];

/** One odometer reading, reduced to what the calculator needs (from a `vehicle-history` entry). */
export interface OdometerReading {
  date: Date;
  odometer: number;
}

/**
 * Where an interval's cycle starts counting from (SWOOP-306). `odometer` is `null` when the vehicle has
 * no reading at all — the distance signal is then not tracked until one arrives.
 */
export interface ServiceReminderBaseline {
  date: Date;
  odometer: number | null;
}

/**
 * The stored due point of a reminder (SWOOP-306), written by `computeDuePoint` on every event.
 *
 * - `dueDate` — baseline date + the interval's months; `null` when the trigger does not use time.
 * - `dueOdometer` — the next fixed km mark above the baseline odometer (multiples of the interval's km:
 *   5,000, 10,000, 15,000… for a 5,000 km interval, SWOOP-309); `null` when the trigger does not use
 *   distance, or when there is no baseline odometer yet.
 * - `projectedDate` — the date the vehicle is expected to reach `dueOdometer` at the tenant default
 *   daily rate (Settings › Maintenance, SWOOP-257). The vehicle's own rate comes with SWOOP-246.
 */
export interface ServiceReminderDuePoint {
  dueDate: Date | null;
  dueOdometer: number | null;
  projectedDate: Date | null;
}

/**
 * One reminder per vehicle × interval of the vehicle's winning plan (SWOOP-247 story 1), stored in the
 * `service-reminders` collection (SWOOP-308). The row lives as long as the vehicle follows that
 * interval and is never deleted.
 *
 * Plan intervals are stored without an `_id`, so the interval is identified by `planId` +
 * `intervalCode` (the code is unique within a plan). Renaming an interval's code therefore cancels the
 * old row and creates a new one.
 */
export interface ServiceReminder extends BaseDocument, ServiceReminderDuePoint {
  baselineDate: Date;
  baselineOdometer: number | null;
  /** When the due point was last recalculated. */
  computedAt: Date;
  /**
   * `nextDueDate` minus the row's grace window; `null` when `nextDueDate` is. Stored so "Due now" is an indexed
   * query (SWOOP-281). Derived: written wherever the due point is (sync, `recordService`, dismiss).
   */
  dueFrom: Date | null;
  /**
   * The plan's `effectiveTo` when the row was last written (SWOOP-308); `null` = open-ended. A row of a
   * lapsed plan stays `ACTIVE` (there is no nightly job), so a consumer of stored rows, such as SWOOP-281's
   * queue, must filter `effectiveTo: null | > now`: a past `effectiveTo` means lapsed, as in `isServicePlanInEffect`.
   * Kept in step with the plan by the sync, which rewrites the row when the plan's `effectiveTo` changes.
   */
  effectiveTo: Date | null;
  intervalCode: string;
  /**
   * The earlier of `dueDate` and `projectedDate`, or whichever exists; `null` when neither does (SWOOP-308).
   * Stored so SWOOP-281 can sort a tenant's queue by it. Derived: only the sync and `recordService` write it.
   */
  nextDueDate: Date | null;
  /** `nextDueDate` at the first snooze of this occurrence; `null` while not snoozed (SWOOP-281). */
  originalDueDate: Date | null;
  /** `service-plans` row id (ObjectId, hex on the wire). */
  planId: string;
  /**
   * The deferred date of a `SNOOZED` reminder (247 story 5: "a snoozed reminder keeps its deferred
   * date, and its underlying due point recalculates beneath it"). Written only by SWOOP-281; the sync
   * (SWOOP-308) recalculates the due point but never touches this field. `null` when not snoozed.
   * SWOOP-291's Scheduled details (planned date, garage) are added by that ticket.
   */
  snoozedUntil: Date | null;
  /**
   * Snoozes of the current occurrence, 0 to `SERVICE_REMINDER_MAX_SNOOZES` (SWOOP-281). Reset when the
   * occurrence restarts; a sync recompute never writes it.
   */
  snoozeCount: number;
  status: ServiceReminderStatus;
  /** The reason of the last snooze or dismiss; `null` after a service is recorded (SWOOP-281). */
  statusReason: string | null;
  /** `vehicles` row id (ObjectId, hex on the wire). */
  vehicleId: string;
  /**
   * The open work order handling this reminder (SWOOP-304; `work-orders` row id, ObjectId hex), set once
   * when a work order is opened from the reminder. `null` (or absent on older rows) while none is open.
   * Stored on the reminder, not on the work order, so one work order can later cover several reminders
   * without a migration. Closing or cancelling the work order clears it (SWOOP-304 × SWOOP-250).
   */
  workOrderId?: string | null;
  /** Display copy of the linked work order's number, e.g. `WO-000031` (SWOOP-304); `null` when none. */
  workOrderNumber?: string | null;
  /**
   * When the reminder was completed (SWOOP-291, WOW 4): the service date (`returnedOn`) of the work order
   * that was closed. Set only on a `COMPLETED` row, `null` (or absent on older rows) on every live row.
   * A `COMPLETED` row keeps its `workOrderId`/`workOrderNumber` as the closing work order. The new cycle's
   * `ACTIVE` row has this `null`, so it is the one the queue, the sync and the vehicle profile see as live.
   */
  completedAt?: Date | null;
  /** The odometer read when the work order closed (`odometerIn`), on a `COMPLETED` row only (SWOOP-291). */
  completedOdometer?: number | null;
  /** The user who closed the completing work order, when known (SWOOP-291); `null` otherwise. */
  completedBy?: string | null;
}

/** Payload for creating a reminder row. Omits the system-managed BaseDocument fields. */
export type CreateServiceReminder = Omit<ServiceReminder, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/** One operation of a reminder's interval, as the vehicle profile lists it (SWOOP-309). */
export interface ServiceReminderOperation {
  /** The service operation's name, resolved from `serviceOperationId`. */
  name: string;
  required: boolean;
}

/**
 * A reminder as the read API returns it (SWOOP-308): the stored row plus read-time projections, none of
 * them stored. The server resolves them from the plan in force, so the profile needs a single call.
 *
 * - `urgency` — derived against today.
 * - `intervalName` — the interval's name.
 * - `intervalRule` — the interval's thresholds and trigger, for "10,000 km / 12 months" (SWOOP-309).
 * - `operations` — the interval's operations, in plan order (SWOOP-309).
 */
export type ServiceReminderView = ServiceReminder & {
  intervalName: string;
  intervalRule: Pick<ServicePlanInterval, 'mileageKm' | 'months' | 'trigger'>;
  operations: ServiceReminderOperation[];
  urgency: ServiceReminderUrgency;
};

/** The minimal identity of a plan, as the vehicle's reminders response names it. */
export type ServicePlanReference = Pick<ServicePlan, 'code' | 'id' | 'name'>;

/**
 * `GET /vehicles/:id/service-reminders` (owned by SWOOP-308, consumed by SWOOP-309).
 *
 * - `planInForce` — the vehicle's winning plan, or `null` when none applies.
 * - `conflict` — set when two plans are equally specific for the vehicle; neither applies then, and
 *   `planInForce` is `null`. The profile says "Plan conflict" and names both.
 * - `latestOdometer` — the vehicle's latest odometer reading in km (same name as on `VehicleListItem`),
 *   or `null` with no reading. The profile needs it for the remaining distance (247 story 9) and for
 *   the Overview alert's "now 24,500 km".
 * - `lastReading` — that same reading with its date, or `null` with no reading. The profile flags a
 *   reading older than 90 days and shows its age on the Maintenance tab (SWOOP-246).
 * - `reminders` — the vehicle's non-cancelled reminders, each with its urgency today.
 */
export interface VehicleServiceRemindersResponse {
  conflict: { plans: ServicePlanReference[] } | null;
  lastReading: { date: string; odometer: number } | null;
  latestOdometer: number | null;
  planInForce: ServicePlanReference | null;
  reminders: ServiceReminderView[];
}
